import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { animate, svg } from 'animejs';
import { SiegeTitle } from '../components/siege/SiegeTitle';

// ─── Silver/steel palette — matches the finalized SIEGE title ───────────
const C = {
  bg: '#0d1117',
  bgPanel: '#111923',
  stone: '#2a323e',
  stoneLight: '#3d4654',
  silver: '#B8C0CC',        // primary tone (same as SiegeTitle)
  silverBright: '#E0E4EC',  // highlight
  silverDim: '#6a7280',     // dim stroke
  shimmerPeak: '#FFFFFF',
  moss: '#7aa55c',          // completed
  mossDark: '#3d5c2d',
  blood: '#b23838',         // danger
  rune: '#5a7cad',
  text: '#d8dce4',
  textDim: '#7a828e',
};

const PX_FONT = "'VT323', monospace";          // body — readable pixel font
const BLOCK_FONT = "'Press Start 2P', monospace"; // tight label font
const TITLE_FONT = "'Metamorphous', serif";    // matches SIEGE typography

// ─── Mock data ──────────────────────────────────────────────────────────
const PHASES = [
  { id: 1, tier: 'SHIELD', accounts: 3, status: 'done' as const },
  { id: 2, tier: 'SHIELD', accounts: 5, status: 'done' as const },
  { id: 3, tier: 'SHIELD', accounts: 7, status: 'done' as const },
  { id: 4, tier: 'SWORD', accounts: 10, status: 'done' as const },
  { id: 5, tier: 'SWORD', accounts: 14, status: 'current' as const },
  { id: 6, tier: 'HAMMER', accounts: 20, status: 'locked' as const },
  { id: 7, tier: 'HAMMER', accounts: 28, status: 'locked' as const },
];

const CLUSTERS = [
  {
    id: 1, label: 'CL-1', cohorts: [
      { id: 'A1', accounts: [{ firm: 'APX', size: '50K', status: 'master' }] },
      { id: 'B1', accounts: [{ firm: 'TRD', size: '50K', status: 'active' }, { firm: 'MFF', size: '50K', status: 'active' }] },
      { id: 'C1', accounts: [{ firm: 'TOP', size: '50K', status: 'danger' }, { firm: 'APX', size: '100K', status: 'active' }] },
    ],
  },
  {
    id: 2, label: 'CL-2', cohorts: [
      { id: 'A2', accounts: [{ firm: 'LUC', size: '50K', status: 'master' }] },
      { id: 'B2', accounts: [{ firm: 'TRD', size: '50K', status: 'active' }, { firm: 'MFF', size: '50K', status: 'active' }] },
      { id: 'C2', accounts: [{ firm: 'TOP', size: '100K', status: 'danger' }, { firm: 'LUC', size: '50K', status: 'active' }] },
    ],
  },
];

const QUESTS = [
  { id: 'payout', name: 'CLAIM THE PAYOUT', status: 'complete' as const, current: 1, goal: 1 },
  { id: 'clean', name: 'CLEAN WINDOW', status: 'active' as const, current: 24, goal: 30 },
  { id: 'volume', name: 'TRADE VOLUME', status: 'active' as const, current: 38, goal: 50 },
];

// ─── Shared shimmer pulse linear gradient (animated) ────────────────────
function ShimmerGradient({ id, vertical = false }: { id: string; vertical?: boolean }) {
  const animate = vertical
    ? { y1: [-40, 140], y2: [-20, 160] }
    : { x1: [-40, 340], x2: [-20, 360] };
  return (
    <motion.linearGradient id={id} gradientUnits="userSpaceOnUse"
      animate={animate}
      transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 4, ease: 'linear' }}>
      <stop stopColor={C.silver} stopOpacity="0" />
      <stop offset="0.4" stopColor={C.silver} stopOpacity="1" />
      <stop offset="0.5" stopColor={C.shimmerPeak} stopOpacity="1" />
      <stop offset="0.6" stopColor={C.silver} stopOpacity="1" />
      <stop offset="1" stopColor={C.silver} stopOpacity="0" />
    </motion.linearGradient>
  );
}

// ─── Pixel Frame (panel with shimmer edge + cobblestone tile pattern) ──
function PixelFrame({ title, children, withShimmer = false }: {
  title?: string;
  children: React.ReactNode;
  withShimmer?: boolean;
}) {
  const uid = useRef(`pf-${Math.random().toString(36).slice(2, 7)}`).current;
  return (
    <div style={{ position: 'relative' }}>
      {/* Cobblestone pattern layer */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.15, pointerEvents: 'none',
        background: `
          repeating-linear-gradient(0deg, transparent 0 11px, ${C.silver}22 11px 12px),
          repeating-linear-gradient(90deg, transparent 0 24px, ${C.silver}22 24px 25px)
        `,
      }} />

      <div style={{
        position: 'relative',
        background: C.bgPanel,
        border: `2px solid ${C.silver}`,
        padding: '16px 20px',
        boxShadow: `0 0 0 2px ${C.bg}, 0 0 0 4px ${C.stone}, 0 0 24px ${C.silver}22`,
      }}>
        {title && (
          <div style={{
            position: 'absolute',
            top: -12,
            left: 20,
            background: C.bg,
            padding: '2px 12px',
            fontFamily: TITLE_FONT,
            fontSize: 14,
            color: C.silverBright,
            letterSpacing: '0.2em',
            textShadow: `0 0 8px ${C.silver}, 0 0 2px ${C.silver}`,
            border: `1px solid ${C.silverDim}`,
          }}>
            {title}
          </div>
        )}

        {/* Shimmer overlay on frame edge */}
        {withShimmer && (
          <svg style={{
            position: 'absolute', inset: -2, width: 'calc(100% + 4px)', height: 'calc(100% + 4px)',
            pointerEvents: 'none',
          }}>
            <defs>
              <ShimmerGradient id={`${uid}-shimmer`} />
            </defs>
            <rect x="0" y="0" width="100%" height="100%" fill="none"
              stroke={`url(#${uid}-shimmer)`} strokeWidth="2" />
          </svg>
        )}

        {children}
      </div>
    </div>
  );
}

// ─── Silver pixel rook — small version for inline character portrait ────
function PixelRook({ size = 48, glow = false }: { size?: number; glow?: boolean }) {
  const uid = useRef(`r-${Math.random().toString(36).slice(2, 6)}`).current;
  const pulseId = `${uid}-pulse`;
  const pix = (x: number, y: number, w: number, h: number, fill: string, glowMe = false) =>
    <rect x={x} y={y} width={w} height={h}
      fill={glowMe && glow ? `url(#${pulseId})` : fill}
      shapeRendering="crispEdges" />;
  return (
    <svg width={size} height={size * 1.25} viewBox="0 0 16 20" shapeRendering="crispEdges">
      <defs>{glow && <ShimmerGradient id={pulseId} />}</defs>
      {pix(2, 0, 2, 2, C.silver, true)}
      {pix(6, 0, 2, 2, C.silver, true)}
      {pix(10, 0, 2, 2, C.silver, true)}
      {pix(12, 0, 2, 2, C.silver, true)}
      {pix(1, 2, 14, 2, C.silver, true)}
      {pix(2, 4, 12, 1, C.stoneLight)}
      {pix(3, 5, 10, 1, C.silver, true)}
      {pix(3, 6, 10, 8, C.silver, true)}
      {pix(3, 9, 10, 1, C.stoneLight)}
      {pix(2, 14, 12, 1, C.silver, true)}
      {pix(1, 15, 14, 2, C.silver, true)}
      {pix(0, 17, 16, 3, C.stoneLight)}
      {pix(0, 19, 16, 1, C.stone)}
    </svg>
  );
}

// ─── Character Card ─────────────────────────────────────────────────────
function CharacterCard() {
  return (
    <PixelFrame title="HERO">
      <div className="flex items-center gap-4">
        <div style={{
          width: 64, height: 76, background: C.stone,
          border: `2px solid ${C.silver}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `inset 0 0 12px ${C.silver}22`,
        }}>
          <PixelRook size={42} glow />
        </div>
        <div className="flex flex-col gap-1">
          <div style={{ fontFamily: TITLE_FONT, fontSize: 22, color: C.silverBright,
            textShadow: `0 0 8px ${C.silver}AA, 0 0 2px ${C.silver}` }}>
            PALADIN
          </div>
          <div style={{ fontFamily: PX_FONT, fontSize: 20, color: C.text, lineHeight: 1 }}>
            Lv.5 · SWORD TIER
          </div>
          <div style={{ fontFamily: PX_FONT, fontSize: 16, color: C.textDim }}>
            PHASE 5 / 7
          </div>
        </div>
      </div>
    </PixelFrame>
  );
}

// ─── Skill Tree with anime.js trace reveal + shimmer ───────────────────
function SkillTree() {
  const svgRef = useRef<SVGSVGElement>(null);
  const [revealed, setRevealed] = useState(false);
  const uid = useRef(`st-${Math.random().toString(36).slice(2, 7)}`).current;
  const pulseId = `${uid}-pulse`;

  const nodeR = 18;
  const phaseY = 50;
  const phaseSpacing = 90;
  const startX = 60;
  const clusterY = 180;
  const cohortY = 270;
  const accountY = 340;
  const currentPhase = PHASES.find(p => p.status === 'current')!;
  const currentIdx = PHASES.indexOf(currentPhase);
  const currentX = startX + currentIdx * phaseSpacing;
  const cluster1X = currentX - 110;
  const cluster2X = currentX + 110;

  useEffect(() => {
    if (!svgRef.current) return;
    const paths = svgRef.current.querySelectorAll(`#${uid}-traceLayer path, #${uid}-traceLayer line, #${uid}-traceLayer rect`);
    if (paths.length === 0) return;
    const drawables = svg.createDrawable(paths as NodeListOf<SVGElement>);
    animate(drawables, {
      draw: ['0 0', '0 1'],
      ease: 'inOutQuad',
      duration: 2000,
      delay: (_el: any, i: number) => i * 30,
      onComplete: () => setRevealed(true),
    });
  }, [uid]);

  const statusColor = (status: 'done' | 'current' | 'locked') => {
    if (status === 'done') return C.moss;
    if (status === 'current') return C.silverBright;
    return C.silverDim;
  };

  // Phase link paths (for trace animation)
  const phaseLinks = PHASES.slice(0, -1).map((p, i) => {
    const x1 = startX + i * phaseSpacing + nodeR;
    const x2 = startX + (i + 1) * phaseSpacing - nodeR;
    const isPast = p.status === 'done' && PHASES[i + 1].status !== 'locked';
    return { d: `M${x1} ${phaseY} L${x2} ${phaseY}`, isPast, key: `pl-${i}` };
  });

  return (
    <svg ref={svgRef} viewBox="0 0 700 400" width="100%" height="440"
      shapeRendering="crispEdges">
      <defs>
        <ShimmerGradient id={pulseId} />
      </defs>

      {/* ─── Phase connection lines ─── */}
      {phaseLinks.map(link => (
        <path key={link.key} d={link.d}
          stroke={link.isPast ? C.moss : C.silverDim}
          strokeWidth={3} fill="none" />
      ))}

      {/* ─── Phase nodes ─── */}
      {PHASES.map((p, i) => {
        const x = startX + i * phaseSpacing;
        const color = statusColor(p.status);
        const isCurrent = p.status === 'current';
        return (
          <g key={p.id}>
            {/* outer frame */}
            <rect x={x - nodeR - 2} y={phaseY - nodeR - 2} width={(nodeR + 2) * 2} height={(nodeR + 2) * 2}
              fill={C.bg} stroke={color} strokeWidth={isCurrent ? 3 : 2} />
            {/* inner square */}
            <rect x={x - nodeR + 3} y={phaseY - nodeR + 3} width={(nodeR - 3) * 2} height={(nodeR - 3) * 2}
              fill={C.stone} />
            {/* current phase shimmer overlay */}
            {isCurrent && revealed && (
              <rect x={x - nodeR - 2} y={phaseY - nodeR - 2} width={(nodeR + 2) * 2} height={(nodeR + 2) * 2}
                fill="none" stroke={`url(#${pulseId})`} strokeWidth={3} />
            )}
            <text x={x} y={phaseY + 5} textAnchor="middle"
              fontFamily={BLOCK_FONT} fontSize={12} fill={color}>
              {p.id}
            </text>
            {p.status === 'done' && (
              <text x={x} y={phaseY + nodeR + 16} textAnchor="middle"
                fontFamily={PX_FONT} fontSize={14} fill={C.moss}>✓</text>
            )}
            {isCurrent && (
              <motion.text x={x} y={phaseY + nodeR + 16} textAnchor="middle"
                fontFamily={PX_FONT} fontSize={16} fill={C.silverBright}
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}>▲</motion.text>
            )}
            {p.status === 'locked' && (
              <text x={x} y={phaseY + nodeR + 16} textAnchor="middle"
                fontFamily={PX_FONT} fontSize={14} fill={C.silverDim}>✕</text>
            )}
            <text x={x} y={phaseY + nodeR + 30} textAnchor="middle"
              fontFamily={BLOCK_FONT} fontSize={6} fill={C.textDim}
              letterSpacing="0.15em">{p.tier}</text>
            <text x={x} y={phaseY + nodeR + 46} textAnchor="middle"
              fontFamily={PX_FONT} fontSize={14} fill={C.text}>
              {p.accounts} ACC
            </text>
          </g>
        );
      })}

      {/* ─── Branches from current phase ─── */}
      <g id={`${uid}-branches`} stroke={C.silver} strokeWidth={3} fill="none">
        <line x1={currentX} y1={phaseY + nodeR + 50} x2={currentX} y2={phaseY + nodeR + 90} />
        <line x1={cluster1X} y1={phaseY + nodeR + 90} x2={cluster2X} y2={phaseY + nodeR + 90} />
        <line x1={cluster1X} y1={phaseY + nodeR + 90} x2={cluster1X} y2={clusterY - 18} />
        <line x1={cluster2X} y1={phaseY + nodeR + 90} x2={cluster2X} y2={clusterY - 18} />
      </g>

      {/* ─── Cluster nodes ─── */}
      {[cluster1X, cluster2X].map((cx, ci) => {
        const cluster = CLUSTERS[ci];
        return (
          <g key={`cl-${ci}`}>
            <rect x={cx - 40} y={clusterY - 16} width={80} height={32}
              fill={C.stone} stroke={C.silver} strokeWidth={2} />
            <rect x={cx - 37} y={clusterY - 13} width={74} height={26}
              fill="none" stroke={C.silverBright} strokeWidth={1} opacity={0.4} />
            <text x={cx} y={clusterY + 5} textAnchor="middle"
              fontFamily={BLOCK_FONT} fontSize={10} fill={C.silverBright}>
              {cluster.label}
            </text>

            <g stroke={C.silver} strokeWidth={2} fill="none">
              <line x1={cx} y1={clusterY + 16} x2={cx} y2={clusterY + 46} />
              {cluster.cohorts.map((_, coi) => {
                const coX = cx + (coi - 1) * 60;
                return (
                  <g key={coi}>
                    {coi !== 1 && (
                      <line x1={cx} y1={clusterY + 46} x2={coX} y2={clusterY + 46} />
                    )}
                    <line x1={coX} y1={clusterY + 46} x2={coX} y2={clusterY + 62} />
                  </g>
                );
              })}
            </g>

            {cluster.cohorts.map((co, coi) => {
              const coX = cx + (coi - 1) * 60;
              return (
                <g key={co.id}>
                  <rect x={coX - 20} y={cohortY - 12} width={40} height={24}
                    fill={C.bgPanel} stroke={C.silver} strokeWidth={1.5} />
                  <text x={coX} y={cohortY + 5} textAnchor="middle"
                    fontFamily={BLOCK_FONT} fontSize={8} fill={C.silver}>
                    {co.id}
                  </text>
                  {co.accounts.map((_, ai) => {
                    const aX = coX + (ai - (co.accounts.length - 1) / 2) * 28;
                    return (
                      <line key={`d-${ai}`} x1={aX} y1={cohortY + 12} x2={aX} y2={accountY - 8}
                        stroke={C.silver} strokeWidth={1.5} />
                    );
                  })}
                  {co.accounts.map((acc, ai) => {
                    const aX = coX + (ai - (co.accounts.length - 1) / 2) * 28;
                    const accColor = acc.status === 'master' ? C.silverBright
                      : acc.status === 'danger' ? C.blood
                      : C.silver;
                    return (
                      <g key={ai}>
                        <rect x={aX - 10} y={accountY - 8} width={20} height={16}
                          fill={C.stone} stroke={accColor} strokeWidth={1.5} />
                        <text x={aX} y={accountY + 4} textAnchor="middle"
                          fontFamily={BLOCK_FONT} fontSize={5} fill={accColor}>
                          {acc.firm}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })}
          </g>
        );
      })}

      {/* ─── Shimmer sweep across whole tree ─── */}
      {revealed && (
        <g stroke={`url(#${pulseId})`} strokeWidth={2} fill="none">
          {phaseLinks.map(link => <path key={`sh-${link.key}`} d={link.d} />)}
        </g>
      )}

      {/* ─── anime.js trace cover layer ─── */}
      <g id={`${uid}-traceCover`} style={{ opacity: revealed ? 0 : 1, transition: 'opacity 0.7s' }}>
        <rect x="0" y="0" width="700" height="400" fill={C.bg} opacity="0.96" />
        <g id={`${uid}-traceLayer`} stroke={C.silver} strokeWidth={2}
          strokeLinecap="round" strokeLinejoin="round" fill="none">
          {phaseLinks.map(link => <path key={`t-${link.key}`} d={link.d} />)}
          <line x1={currentX} y1={phaseY + nodeR + 50} x2={currentX} y2={phaseY + nodeR + 90} />
          <line x1={cluster1X} y1={phaseY + nodeR + 90} x2={cluster2X} y2={phaseY + nodeR + 90} />
          <line x1={cluster1X} y1={phaseY + nodeR + 90} x2={cluster1X} y2={clusterY - 18} />
          <line x1={cluster2X} y1={phaseY + nodeR + 90} x2={cluster2X} y2={clusterY - 18} />
        </g>
      </g>

      <text x={350} y={15} textAnchor="middle"
        fontFamily={TITLE_FONT} fontSize={14} fill={C.silver}
        letterSpacing="0.3em" style={{ textShadow: `0 0 6px ${C.silver}AA` }}>
        ── CAMPAIGN MAP ──
      </text>
    </svg>
  );
}

// ─── Pixel XP Bar ───────────────────────────────────────────────────────
function PixelBar({ current, goal, color }: { current: number; goal: number; color: string }) {
  const pct = Math.min(1, current / goal);
  const segments = 20;
  const filled = Math.round(pct * segments);
  return (
    <div className="flex items-center gap-2">
      <div style={{
        display: 'flex', gap: 1, padding: 2, background: C.bg,
        border: `1px solid ${C.stone}`,
      }}>
        {Array.from({ length: segments }).map((_, i) => (
          <div key={i} style={{
            width: 8, height: 14,
            background: i < filled ? color : C.stone,
            boxShadow: i < filled
              ? `inset -1px -1px 0 rgba(0,0,0,0.3), inset 1px 1px 0 ${C.shimmerPeak}40`
              : 'none',
          }} />
        ))}
      </div>
      <div style={{ fontFamily: PX_FONT, fontSize: 18, color: C.text }}>
        {current}/{goal}
      </div>
    </div>
  );
}

// ─── Quest Log ──────────────────────────────────────────────────────────
function QuestLog() {
  return (
    <PixelFrame title="QUEST LOG">
      <div className="flex flex-col gap-3">
        {QUESTS.map(q => (
          <div key={q.id} className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span style={{
                fontFamily: PX_FONT, fontSize: 22,
                color: q.status === 'complete' ? C.moss : C.silverBright,
                textShadow: q.status !== 'complete' ? `0 0 6px ${C.silver}` : 'none',
              }}>
                {q.status === 'complete' ? '✓' : '⚔'}
              </span>
              <div>
                <div style={{
                  fontFamily: TITLE_FONT, fontSize: 16,
                  color: q.status === 'complete' ? C.moss : C.text,
                  letterSpacing: '0.08em',
                }}>
                  {q.name}
                </div>
                <div style={{ fontFamily: PX_FONT, fontSize: 14, color: C.textDim }}>
                  {q.status === 'complete' ? 'REWARD CLAIMED' : 'IN PROGRESS'}
                </div>
              </div>
            </div>
            <PixelBar current={q.current} goal={q.goal}
              color={q.status === 'complete' ? C.moss : C.silver} />
          </div>
        ))}
      </div>
    </PixelFrame>
  );
}

// ─── Inventory ──────────────────────────────────────────────────────────
function Inventory() {
  const allAccounts = CLUSTERS.flatMap(c => c.cohorts.flatMap(co =>
    co.accounts.map(a => ({ ...a, cohort: co.id, cluster: c.label }))
  ));
  return (
    <PixelFrame title="INVENTORY">
      <div className="grid grid-cols-7 gap-2">
        {allAccounts.map((acc, i) => {
          const accColor = acc.status === 'master' ? C.silverBright
            : acc.status === 'danger' ? C.blood : C.silver;
          return (
            <div key={i} style={{
              padding: 8, background: C.stone,
              border: `2px solid ${accColor}`,
              boxShadow: `inset 1px 1px 0 ${C.stoneLight}, inset -1px -1px 0 ${C.bg}, 0 0 8px ${accColor}33`,
            }}>
              <div style={{
                fontFamily: BLOCK_FONT, fontSize: 8, color: accColor, textAlign: 'center',
              }}>
                {acc.firm}
              </div>
              <div style={{
                fontFamily: PX_FONT, fontSize: 14, color: C.text, textAlign: 'center', marginTop: 2,
              }}>
                ${acc.size}
              </div>
              <div style={{
                fontFamily: PX_FONT, fontSize: 12, color: C.textDim, textAlign: 'center',
              }}>
                {acc.cohort}
              </div>
            </div>
          );
        })}
      </div>
    </PixelFrame>
  );
}

// ─── Main Preview Page ──────────────────────────────────────────────────
export function SkilltreePreview() {
  return (
    <div style={{
      minHeight: '100vh',
      background: `
        radial-gradient(ellipse at top, ${C.silver}12 0%, transparent 50%),
        repeating-linear-gradient(45deg, ${C.bg} 0px, ${C.bg} 2px, #121820 2px, #121820 4px)
      `,
      color: C.text,
      padding: '24px 32px 48px',
      fontFamily: PX_FONT,
    }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* ─── TOP BANNER — the finalized SIEGE title (castle + plaque + shimmer + Metamorphous) ─── */}
        <div className="mb-8">
          <SiegeTitle />
        </div>

        {/* Hero + Orders row */}
        <div className="grid grid-cols-[auto_1fr] gap-6 mb-8">
          <CharacterCard />
          <PixelFrame title="DAILY ORDERS">
            <div style={{ fontFamily: PX_FONT, fontSize: 20, color: C.text, lineHeight: 1.3 }}>
              ⚔ Hold SWORD tier through Phase 5.<br />
              ⚔ Complete 2 remaining gates to unlock HAMMER.<br />
              ⚔ 14/14 accounts deployed across 2 clusters.
            </div>
          </PixelFrame>
        </div>

        {/* Skill Tree */}
        <div className="mb-8">
          <PixelFrame title="CAMPAIGN MAP" withShimmer>
            <SkillTree />
          </PixelFrame>
        </div>

        {/* Quest Log */}
        <div className="mb-8">
          <QuestLog />
        </div>

        {/* Inventory */}
        <div className="mb-8">
          <Inventory />
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          fontFamily: TITLE_FONT,
          fontSize: 12,
          color: C.textDim,
          letterSpacing: '0.3em',
          marginTop: 24,
        }}>
          ═══ HONOR THY SETUP ═══
        </div>
      </div>
    </div>
  );
}
